// No hooks and no store reads, so this stays unit-testable. Product entries read the
// portal products; every other gate reads config, tier, or permissions.

import type {
  AppShellSidebarSectionData,
  AppShellSidebarEntryData,
  AppShellSidebarItemData,
  NavIcon,
} from '@hopdrive/feature-app-shell';
import type { PortalProducts } from '@features/products/types';
import { PRODUCT_LABELS, resolveMovesLabel } from '@features/products/ui/productCopy';

import { canHaveDrivers } from '@utils/tiers';
import { isProductOwned, isProductOffered } from '@features/products/productGates';
import { resolveProgramVisuals } from '@features/programs/programVisuals';

import {
  BiBorderAll,
  BiClipboard,
  BiSolidBook,
  BiSolidCalendar,
  BiSolidCar,
  BiSolidCategory,
  BiSolidCreditCard,
  BiSolidGroup,
  BiSolidMapPin,
  BiSolidMessageRoundedDetail,
  BiSolidReceipt,
  BiSolidTime,
} from 'react-icons/bi';
import {
  PiCarFill,
  PiCertificateFill,
  PiHandWavingFill,
  PiHouseFill,
  PiPackageFill,
  PiSteeringWheelFill,
  PiWrenchFill,
} from 'react-icons/pi';
import { productRootPath } from '@features/products/productRoutes';

// INPUT CONTRACT ---------------------------------------------------------------- //

/** Feature-permission gates, pre-resolved by the app so this resolver stays free
 *  of dealer's `useFeaturePermission` / `FeatureName` coupling. The app maps each
 *  `FeatureName` to the matching field before calling in. */
export interface NavPermissions {
  canPlanMoves: boolean;
  canManageUsers: boolean;
  canManageDrivers: boolean;
  canViewInvoices: boolean;
  canViewAppraisals: boolean;
  canViewMobileService: boolean;
  canViewPartsDelivery: boolean;
}

/** The rooftop fields the nav gates read. `config` is the raw (unparsed) config;
 *  `effective_config` is the merged/effective one. */
export interface NavRooftop {
  tier?: string | null;
  effective_config?: Record<string, any> | null;
  config?: string | Record<string, any> | null;
}

export interface ResolveNavModelInput {
  user?: { profile?: { default_role?: string | null } | null } | null;
  /** The active customer's composed portal products; undefined until they have loaded,
   *  which reads as no product shown. */
  products?: PortalProducts;
  rooftop?: NavRooftop | null;
  /** The customer's effective config — most enablement flags live here. */
  customerConfig?: Record<string, any> | null;
  permissions: NavPermissions;
  /** True while rooftop data is still loading; suppresses the rooftop-dependent
   *  entries so they don't flash before the rooftop is known. */
  rooftopLoading: boolean;
  /** The working customer's Valet is self-managed-capable (self-managed or mixed), from the
   *  product accessor. Drives the self-managed nav gate; the app supplies it. */
  isSelfManaged: boolean;
  /** How many moves and stops the Missing Ref Report lists. Above zero, its entry carries a badge. */
  missingRefCount?: number;
}

// The roles that see the rooftop overview page regardless of tier.
const ORG_VIEW_ROLES = new Set(['dealer-super-admin', 'dealer-admin', 'program-admin']);

// ENTRY CONSTRUCTORS ---------------------------------------------------------------- //

/** A route entry as a group child: the shell renders group children as bare items, so
 *  they carry no `type` discriminant. */
const childItem = (label: string, route: string, icon: NavIcon): Omit<AppShellSidebarItemData, 'type'> => ({
  label,
  route,
  icon,
});

const item = (label: string, route: string, icon: NavIcon): AppShellSidebarItemData => ({
  type: 'item',
  ...childItem(label, route, icon),
});

// DERIVED FLAGS ---------------------------------------------------------------- //

/** Read `appointment_management.enabled` off the rooftop's RAW config, which may
 *  arrive as a JSON string. Fails closed on absent/invalid config. */
function resolveAppointmentManagementEnabled(config: NavRooftop['config']): boolean {
  if (!config) return false;
  try {
    const parsed = typeof config === 'string' ? JSON.parse(config) : config;
    return parsed?.appointment_management?.enabled === true;
  } catch {
    return false;
  }
}

interface NavFlags {
  showMissingRefReport: boolean;
  missingRefLabel: string;
  showPrograms: boolean;
  showAppointmentTab: boolean;
  appointmentManagementEnabled: boolean;
  isLite: boolean;
  canDrive: boolean;
  isOrgViewRole: boolean;
}

function deriveNavFlags(input: ResolveNavModelInput): NavFlags {
  const { rooftop, customerConfig, user } = input;
  const effectiveConfig = rooftop?.effective_config;

  return {
    showMissingRefReport: customerConfig?.reference_num_report_enabled || false,
    missingRefLabel: customerConfig?.reference_num_report_label || 'Missing Ref Report',
    showPrograms: resolveProgramVisuals(customerConfig).active === true,
    showAppointmentTab: effectiveConfig?.appointments?.enabled === true,
    appointmentManagementEnabled: resolveAppointmentManagementEnabled(rooftop?.config),
    isLite: input.isSelfManaged,
    canDrive: canHaveDrivers(rooftop),
    isOrgViewRole: ORG_VIEW_ROLES.has(user?.profile?.default_role ?? ''),
  };
}

// SECTION BUILDERS ---------------------------------------------------------------- //
// Order within each array is the render order.

function helpEntries(): AppShellSidebarEntryData[] {
  const entries: AppShellSidebarEntryData[] = [];
  entries.push(item('Training', '/learning-center', BiSolidBook));
  entries.push(item('Support', '/support', BiSolidMessageRoundedDetail));
  return entries;
}

/** Drop the gated-off (null) sections from an assembled list. */
function withoutNull(sections: (AppShellSidebarSectionData | null)[]): AppShellSidebarSectionData[] {
  return sections.filter((s): s is AppShellSidebarSectionData => s !== null);
}

/** In render order. */
const PRODUCT_SECTIONS = {
  myProducts: 'My Products',
  discover: 'Discover',
  operations: 'Operations',
  manage: 'Manage',
  account: 'Account',
} as const;

/** A section, or null when it has no visible entries so the sidebar skips it. */
const sectionOf = (title: string, entries: AppShellSidebarEntryData[]): AppShellSidebarSectionData | null =>
  entries.length > 0 ? { title, entries } : null;

/** The "My Products" entries: one item per product the customer owns, each landing on its
 *  product root where the product shell carries the product's pages as tabs, in canonical order.
 *  Valet is owned like any other product (a customer may hold no Valet product), so it gates on
 *  ownership too. The active-entry rule lights an item on every path beneath its root, so a tab
 *  or detail page keeps its product lit. */
function myProductsEntries(input: ResolveNavModelInput): AppShellSidebarEntryData[] {
  const entries = input.products?.entries;
  const list: AppShellSidebarEntryData[] = [];
  if (isProductOwned(entries?.moves))
    list.push(item(resolveMovesLabel(entries?.moves?.heldModes), productRootPath('moves'), PiCarFill));
  if (isProductOwned(entries?.['mobile-service']))
    list.push(item(PRODUCT_LABELS['mobile-service'], '/mobile-service', PiWrenchFill));
  if (isProductOwned(entries?.['parts-delivery']))
    list.push(item(PRODUCT_LABELS['parts-delivery'], '/parts-delivery', PiPackageFill));
  if (isProductOwned(entries?.['guest-rides']))
    list.push(item(PRODUCT_LABELS['guest-rides'], '/guest-rides', PiHandWavingFill));
  if (isProductOwned(entries?.appraisals)) list.push(item(PRODUCT_LABELS.appraisals, '/appraisals', PiCertificateFill));
  return list;
}

/** The "Discover" entries: each marketing-state product in canonical order, landing on its own page
 *  (whose state boundary renders the marketing panel). These are products the customer does not hold
 *  but is offered — the nav counterpart of the Home discover hero. Valet appears here too when the
 *  customer holds no Valet product and it resolves to the marketing state. */
function discoverEntries(products: PortalProducts | undefined): AppShellSidebarEntryData[] {
  const entries = products?.entries;
  const list: AppShellSidebarEntryData[] = [];
  if (isProductOffered(entries?.moves))
    list.push(item(resolveMovesLabel(entries?.moves?.heldModes), productRootPath('moves'), PiCarFill));
  if (isProductOffered(entries?.['mobile-service']))
    list.push(item(PRODUCT_LABELS['mobile-service'], '/mobile-service', PiWrenchFill));
  if (isProductOffered(entries?.['parts-delivery']))
    list.push(item(PRODUCT_LABELS['parts-delivery'], '/parts-delivery', PiPackageFill));
  if (isProductOffered(entries?.['guest-rides']))
    list.push(item(PRODUCT_LABELS['guest-rides'], '/guest-rides', PiHandWavingFill));
  if (isProductOffered(entries?.appraisals))
    list.push(item(PRODUCT_LABELS.appraisals, '/appraisals', PiCertificateFill));
  return list;
}

/** The "Operations" entries: the cross-product and cross-rooftop views of the customer's work,
 *  ordered upcoming to past. */
function operationsEntries(input: ResolveNavModelInput, f: NavFlags): AppShellSidebarEntryData[] {
  const list: AppShellSidebarEntryData[] = [];
  if (f.showAppointmentTab) list.push(item('Appointments', '/appointments', BiSolidCalendar));
  if (f.isOrgViewRole || (!input.rooftopLoading && f.isLite)) {
    list.push(item('Rooftop Overview', '/organization-view', BiBorderAll));
  }
  if (f.showMissingRefReport) {
    const missingRef = item(f.missingRefLabel, '/missing-ref', BiClipboard);
    if ((input.missingRefCount ?? 0) > 0) missingRef.badgeCount = input.missingRefCount;
    list.push(missingRef);
  }
  return list;
}

/** The "Manage" entries: the people, fleet, places, and operating hours the customer
 *  maintains. Drivers waits on the rooftop so the tier-dependent entry never flashes. */
function manageEntries(input: ResolveNavModelInput, p: NavPermissions, f: NavFlags): AppShellSidebarEntryData[] {
  const entries = input.products?.entries;
  const list: AppShellSidebarEntryData[] = [];
  if (p.canManageDrivers && !input.rooftopLoading && f.canDrive) {
    list.push(item('Drivers', '/drivers', PiSteeringWheelFill));
  }
  if (p.canManageUsers) list.push(item('Users', '/users', BiSolidGroup));
  if (isProductOwned(entries?.['mobile-service']) || isProductOwned(entries?.['parts-delivery'])) {
    list.push(item('Vehicles', '/service-vehicles', BiSolidCar));
  }
  list.push(item('Locations', '/locations', BiSolidMapPin));
  if (f.appointmentManagementEnabled && f.isLite) list.push(item('Hours', '/settings', BiSolidTime));
  return list;
}

/** The "Account" entries: the customer's commercial relationship with HopDrive. */
function accountEntries(p: NavPermissions, f: NavFlags): AppShellSidebarEntryData[] {
  const list: AppShellSidebarEntryData[] = [];
  if (p.canViewInvoices) {
    list.push(item('Invoices', '/invoices', BiSolidReceipt));
    list.push(item('Payment Methods', '/billing-preferences', BiSolidCreditCard));
  }
  if (f.showPrograms) list.push(item('Program Coverage', '/programs', BiSolidCategory));
  return list;
}

// RESOLVER ---------------------------------------------------------------- //

/** Home's row: the title-less top row above the sections. Home points at the root so every Home tab
 *  lights it (the filled Phosphor mark matches the product entries); Training and Support sit
 *  beneath it. */
const homeSection = (): AppShellSidebarSectionData => ({
  entries: [item('Home', '/home', PiHouseFill), ...helpEntries()],
});

/** Valet's pages have no entries of their own. Orders, Schedule, Moves, and Rideshares are
 *  Valet's tabs, and the move planner and the CSV import are Valet's header actions. */
function productSections(input: ResolveNavModelInput, p: NavPermissions, f: NavFlags): AppShellSidebarSectionData[] {
  return withoutNull([
    homeSection(),
    sectionOf(PRODUCT_SECTIONS.myProducts, myProductsEntries(input)),
    sectionOf(PRODUCT_SECTIONS.discover, discoverEntries(input.products)),
    sectionOf(PRODUCT_SECTIONS.operations, operationsEntries(input, f)),
    sectionOf(PRODUCT_SECTIONS.manage, manageEntries(input, p, f)),
    sectionOf(PRODUCT_SECTIONS.account, accountEntries(p, f)),
  ]);
}

export function resolveNavModel(input: ResolveNavModelInput): AppShellSidebarSectionData[] {
  const { permissions: p } = input;
  const f = deriveNavFlags(input);
  return productSections(input, p, f);
}
